export type LegalSection = {
  id: string
  number: string
  heading: string
  paragraphs?: string[]
  bullets?: string[]
  paragraphs2?: string[]
  subheads?: { heading: string; paragraphs?: string[]; bullets?: string[] }[]
}

export type LegalDoc = {
  key: 'privacy' | 'terms' | 'cookies'
  title: string
  shortTitle: string
  documentId: string
  revision: string
  effective: string
  updated: string
  classification: string
  summary: string
  sections: LegalSection[]
}

export const legalDocs: Record<LegalDoc['key'], LegalDoc> = {
  privacy: {
    key: 'privacy',
    title: 'Privacy Policy',
    shortTitle: 'Privacy',
    documentId: 'OB-LEG-PRIV-001',
    revision: 'Rev 2.0',
    effective: 'August 14, 2026',
    updated: 'August 14, 2026',
    classification: 'Public — Oilfield Operations Software',
    summary:
      'This Privacy Policy describes how OilBuddy AI collects, uses, discloses, and protects personal information and oilfield operational records when you use oilbuddy.ai, related mobile applications, and connected field tools. It applies to operators, service companies, independent contractors, company men, W-2 employees, supervisors, and drivers.',
    sections: [
      {
        id: 'intro',
        number: '1',
        heading: 'Introduction and scope',
        paragraphs: [
          'OilBuddy AI (“OilBuddy,” “Company,” “we,” “us,” or “our”) provides a software platform for oil and gas field operations, including but not limited to crew dispatch, field tickets, tank watch and strap records, well-head sheets, daily reports, invoicing, contractor and employee payroll, safety documentation, and related workflow tools.',
          'This Policy applies to personal information and operational data processed through the OilBuddy websites, web application, APIs, and any field or driver applications we make available. It does not apply to third-party websites or operator systems that we do not control, even if linked from the Service.',
          'Oilfield work involves both personal data (who you are and how you are paid) and industrial / operational data (what happened on a location). Both categories are covered here because they are often stored on the same ticket, tank sheet, or daily report.',
        ],
      },
      {
        id: 'controller',
        number: '2',
        heading: 'Who is responsible for your information',
        paragraphs: [
          'For accounts you create directly with OilBuddy, OilBuddy is the business that determines how personal information is processed.',
          'When a service company, operator, or other organization invites you onto its OilBuddy workspace (for example as an employee, driver, company man, or assigned contractor), that organization is typically the primary controller of workplace and location records. OilBuddy processes that information on the organization’s instructions to provide the Service. In that case, requests about employment files, location access, or operator-required records should also be directed to that organization.',
        ],
        bullets: [
          'Service operator: OilBuddy AI, accessible at oilbuddy.ai',
          'Privacy and legal notices: contact@oilbuddy.ai',
          'Support: contact@oilbuddy.ai or the in-app Support Center',
        ],
      },
      {
        id: 'roles',
        number: '3',
        heading: 'User roles this Policy covers',
        paragraphs: [
          'OilBuddy is built around oilfield roles. The information we hold, and who can see it, depends on the role attached to your account:',
        ],
        bullets: [
          'Independent contractor / 1099 service provider — profile, tickets, invoices you issue, timesheets, expenses, job applications, and payouts.',
          'Oilfield service company (operator of a company workspace) — company profile, crew, jobs, dispatch, approved tickets, company invoices, payroll, tanks, and customer records.',
          'W-2 employee and supervisor — timesheets, assigned dispatch, field tickets, tank and well sheets, daily reports, and safety submissions tied to the employer workspace.',
          'Driver — driver calls, haul / load tickets, tank watch flags, and assigned locations.',
          'Company man / field representative — ticket review and approval, location reports, and tank visibility granted by the service company or operator.',
        ],
      },
      {
        id: 'categories',
        number: '4',
        heading: 'Categories of information we collect',
        subheads: [
          {
            heading: '4.1 Identity and account information',
            bullets: [
              'Name, email address, phone number, password or authentication token, and user type.',
              'Company name, invite codes, crew level (for example supervisor or driver), certifications listed on a profile, and profile photos or logos you upload.',
              'Job marketplace information such as skills, experience, availability, and applications.',
            ],
          },
          {
            heading: '4.2 Employment, contractor, and payroll information',
            bullets: [
              'Hire date, position, hourly or contract rate, employment type, and payroll records (gross, net, status, pay date).',
              'Timesheets, hours worked, job or location references, and supervisor approvals.',
              'Tax-related workspace settings. We are not your CPA; W-2 / 1099 filings remain your or your company’s responsibility.',
            ],
          },
          {
            heading: '4.3 Field operations and production-adjacent records',
            paragraphs: [
              'These records may include personal names (who signed a ticket) together with location and process data. Examples include:',
            ],
            bullets: [
              'Field tickets: ticket number, customer / operator name, well name, lease or pad, county and state, PO / AFE / job numbers, service type, labor, equipment, materials, rates, signatures, and notes.',
              'Dispatch assignments: date, start and end time, site, contractor or employee assignment, and status.',
              'Tank records: tank identity, location, strap or visual level, barrels, watch flags (high, haul, empty, in use), load / haul events, and related photos or remarks.',
              'Well-head sheets and daily reports: pressures, temperatures, rates, comments, and supervisor sign-off as entered by your crew.',
              'Work orders, sites, trucking / loads, rentals, parts, chemicals, and JSA or safety attachments where those modules are used.',
            ],
          },
          {
            heading: '4.4 Safety, HSE, and incident information',
            paragraphs: [
              'If you or your company use OilBuddy for safety or compliance workflows, we may process incident descriptions, near-miss reports, JSA / FLRA content, certification status you choose to store, and related attachments. This information can be sensitive. It is used to operate the tools your organization enabled, not to sell advertising.',
            ],
          },
          {
            heading: '4.5 Commercial and payment information',
            bullets: [
              'Invoices, line items, amounts, due dates, payment status, client names, and ticket-to-invoice links.',
              'Recorded receipts, payment method type (for example ACH or check), and reference numbers.',
              'Store or marketplace purchases processed through OilBuddy or a payment processor. Full payment-card numbers are handled by the processor, not stored by OilBuddy as PAN data.',
            ],
          },
          {
            heading: '4.6 Technical and security information',
            bullets: [
              'IP address, browser and device type, approximate time of access, and pages or API routes requested.',
              'Authentication tokens, session identifiers, and security logs used to detect abuse.',
              'Crash or error diagnostics if you or your browser report them.',
            ],
          },
        ],
      },
      {
        id: 'sources',
        number: '5',
        heading: 'How we collect information',
        bullets: [
          'Directly from you when you register, complete a ticket, strap a tank, submit a timesheet, create an invoice, or contact support.',
          'From your employer, the service company that assigned you, or a company man who reviews your work — for example when they create dispatch or approve a ticket.',
          'From devices and browsers automatically, as described in our Cookie Policy.',
          'From optional integrations you or your company connect (for example accounting, messaging, or payment tools). Those providers have their own policies.',
          'From publicly available or customer-provided well, lease, or site identifiers that you choose to enter. OilBuddy does not independently scrape state commission well files unless a future integration is clearly disclosed and enabled by you.',
        ],
      },
      {
        id: 'use',
        number: '6',
        heading: 'How we use information',
        paragraphs: [
          'We use personal and operational information only as needed to provide, secure, and improve OilBuddy, and to meet legal duties. Typical purposes include:',
        ],
        bullets: [
          'Creating and securing accounts; enforcing role-based access so a driver does not see company payroll, and a contractor sees their own invoices.',
          'Running field workflows: tickets, approvals, tank watch, well sheets, daily reports, and dispatch.',
          'Generating contractor and company invoices, including converting an approved or contractor-owned field ticket into an invoice.',
          'Calculating or displaying payroll and timesheet totals for the employer or contractor who owns those records.',
          'Customer support, troubleshooting, and service notices (password resets, ticket status, billing).',
          'Preventing fraud, unauthorized access, and misuse of location or financial data.',
          'Complying with law, responding to valid legal process, and keeping records that oilfield commercial practice or tax rules require.',
          'Improving reliability and features. If we use aggregated analytics, we do so in a form that is not intended to identify a specific worker on a specific well.',
        ],
      },
      {
        id: 'ai',
        number: '7',
        heading: 'Artificial intelligence features',
        paragraphs: [
          'Some OilBuddy features use automated or AI-assisted tools (for example messaging assistance, tax-oriented summaries, or operational suggestions). Those features operate on information you or your organization already stored in OilBuddy or typed into the tool.',
          'AI output is assistive only. It is not a substitute for a licensed professional engineer, petroleum engineer, tax advisor, attorney, or certified HSE professional. You must verify any AI-generated schedule, invoice description, tax figure, or safety suggestion before relying on it in the field or in a filing.',
          'We do not use your identifiable well, ticket, or payroll records to train public general-purpose models for unrelated third parties. If a specific AI feature sends content to a subprocessors’ model API, that transfer is limited to providing the feature you invoked and is covered by our processor agreements.',
        ],
      },
      {
        id: 'sharing',
        number: '8',
        heading: 'When we share information',
        paragraphs: [
          'We do not sell personal information. We do not share oilfield operational records for advertising. We disclose information only as follows:',
        ],
        subheads: [
          {
            heading: '8.1 Inside your working relationship',
            paragraphs: [
              'A field ticket, tank sheet, or invoice is often meant to be seen by more than one party. Depending on permissions, records may be visible to the creating worker, the assigned contractor, the service company, a reviewing company man, and (where invoiced) the billing customer named on the record. That visibility is the point of the product.',
            ],
          },
          {
            heading: '8.2 Service providers',
            paragraphs: [
              'We use vendors for hosting, email delivery, payment processing, error monitoring, and optional integrations (such as QuickBooks, Slack, Stripe, or store / affiliate product listings). They may process data only to perform services for us and are required to protect it.',
            ],
          },
          {
            heading: '8.3 Legal, safety, and emergency',
            paragraphs: [
              'We may disclose information if we reasonably believe it is required by law, regulation, or legal process; to protect the rights, safety, or property of OilBuddy, our users, or the public; or in connection with a suspected security incident. Nothing in this Policy limits a party’s independent duty to report a spill, injury, or regulatory event to the proper agency.',
            ],
          },
          {
            heading: '8.4 Corporate transactions',
            paragraphs: [
              'If OilBuddy is involved in a merger, acquisition, financing, or sale of assets, information may be transferred as part of that transaction under continuing confidentiality obligations.',
            ],
          },
        ],
      },
      {
        id: 'operator-data',
        number: '9',
        heading: 'Operator, lease, and well information',
        paragraphs: [
          'Users often enter operator names, well names, API-style identifiers, lease or pad names, AFE and PO numbers, and production-adjacent readings. That information may be confidential to the operator or service company under a master service agreement (MSA), confidentiality agreement, or site access rules.',
          'OilBuddy treats such entries as customer content. We do not publish well or tank data to other customers’ workspaces. You and your organization are responsible for entering only data you are allowed to store in a contractor / company system and for setting the correct user permissions.',
          'OilBuddy is not the operator of record, is not a midstream or production accountant, and does not file state oil and gas commission reports on your behalf unless a future feature is expressly contracted in writing.',
        ],
      },
      {
        id: 'retention',
        number: '10',
        heading: 'Retention of oilfield and business records',
        paragraphs: [
          'Retention follows the purpose of the record and, where a company workspace controls the data, that company’s instructions. As a general practice:',
        ],
        bullets: [
          'Account and authentication data — kept while the account is active and for a reasonable period after closure to prevent fraud and complete outstanding support.',
          'Field tickets, invoices, and payment records — commonly retained at least seven (7) years to support commercial disputes, accounts receivable, and tax audit, unless a shorter period is legally required or a longer period is set by your company.',
          'Timesheets and payroll — retained as needed for wage, tax, and employment-record duties of the employer or contractor.',
          'Tank, well-head, and daily operational sheets — retained for the life of the workspace plus a period sufficient for handover, incident review, and customer audit, typically three (3) to seven (7) years unless the customer contract requires longer.',
          'Safety incidents and JSAs — often retained longer than routine tickets because of OSHA, insurance, and litigation hold needs. Your organization should set its own HSE retention schedule.',
          'Server logs — typically rotated on a shorter cycle unless needed for security investigation.',
        ],
        paragraphs2: [
          'If you request deletion, we will delete or de-identify personal information we control except where we must keep a record (for example an issued invoice, a paid payroll entry, a legal hold, or a ticket that is part of another party’s audit file).',
        ],
      },
      {
        id: 'security',
        number: '11',
        heading: 'Security',
        paragraphs: [
          'We use administrative, technical, and physical safeguards appropriate to a multi-tenant oilfield operations system, including encrypted transport (HTTPS), authenticated APIs, role-based access, and hashed or tokenized credentials.',
          'No method of transmission or storage is completely secure. Location, ticket, and financial data can cause significant harm if misused. You must use unique passwords, sign out of shared doghouse or trailer computers, and grant company-man or contractor access only to people who need it.',
          'Report suspected unauthorized access immediately to contact@oilbuddy.ai and to your company administrator.',
        ],
      },
      {
        id: 'rights',
        number: '12',
        heading: 'Your privacy rights',
        paragraphs: [
          'Depending on your state of residence (including rights similar to those under the California Consumer Privacy Act, as amended, and other U.S. state privacy laws), you may have the right to:',
        ],
        bullets: [
          'Know and access the personal information we hold about you.',
          'Correct inaccurate personal information.',
          'Request deletion, subject to the retention exceptions in Section 10. Submit a request at oilbuddy.ai/#/data-deletion or email contact@oilbuddy.ai with the subject “Data deletion request.”',
          'Opt out of “sale” or “sharing” of personal information. OilBuddy does not sell personal information and does not share it for cross-context behavioral advertising.',
          'Appeal a denial of a privacy request by emailing contact@oilbuddy.ai with the subject line “Privacy Appeal.”',
        ],
        paragraphs2: [
          'We will not discriminate against you for exercising these rights. We may need to verify your identity and, for workplace records, may route the request to the company that owns the workspace. Authorized agents may submit requests with proof of authority.',
        ],
      },
      {
        id: 'children',
        number: '13',
        heading: 'Children and field age restrictions',
        paragraphs: [
          'OilBuddy is a business platform for adult oilfield workers and companies. The Service is not directed to children under 16, and we do not knowingly collect personal information from children. Oilfield locations are inherently hazardous; account holders must meet applicable employment and site-access age requirements independently of this Policy.',
        ],
      },
      {
        id: 'transfers',
        number: '14',
        heading: 'Storage location',
        paragraphs: [
          'OilBuddy is operated for customers in the United States, with a focus on onshore oilfield basins including the Williston Basin / Bakken and similar Lower-48 operations. Data is hosted on infrastructure in the United States unless we notify you otherwise. If you access the Service from another country, you understand that your information will be processed in the United States, which may have different data-protection rules than your home jurisdiction.',
        ],
      },
      {
        id: 'cookies-ref',
        number: '15',
        heading: 'Cookies and similar technologies',
        paragraphs: [
          'We use cookies, local storage, and similar technologies for authentication, role preference, and service reliability. Details, including how to control them, are in the Cookie Policy, which is incorporated into this Privacy Policy.',
        ],
      },
      {
        id: 'changes',
        number: '16',
        heading: 'Changes to this Policy',
        paragraphs: [
          'We may revise this Policy as the Service or the law changes. The “Last updated” and revision fields at the top of this document will change when we do. Material changes will be posted on this page and, where appropriate, noted in the product or by email to the account address. Continued use after the effective date constitutes acceptance of the updated Policy.',
        ],
      },
      {
        id: 'contact',
        number: '17',
        heading: 'How to contact us',
        paragraphs: [
          'For privacy requests, questions about field-record access, or to report a data incident:',
        ],
        bullets: [
          'Email: contact@oilbuddy.ai',
          'Web: oilbuddy.ai/#/contact',
          'Data deletion request: oilbuddy.ai/#/data-deletion (document OB-LEG-DEL-001)',
          'Document reference: include OB-LEG-PRIV-001 and your account email in the message.',
        ],
      },
    ],
  },
  terms: {
    key: 'terms',
    title: 'Terms of Service',
    shortTitle: 'Terms',
    documentId: 'OB-LEG-TOS-001',
    revision: 'Rev 2.0',
    effective: 'August 14, 2026',
    updated: 'August 14, 2026',
    classification: 'Public — Binding Terms for OilBuddy AI',
    summary:
      'These Terms of Service govern access to and use of the OilBuddy AI platform by oilfield service companies, operators’ field representatives, independent contractors, employees, supervisors, and drivers. They allocate responsibility for field data accuracy, safety, invoicing, and regulatory compliance. They do not replace your MSA, work order, or site-specific HSE rules.',
    sections: [
      {
        id: 'acceptance',
        number: '1',
        heading: 'Acceptance of terms',
        paragraphs: [
          'By creating an account, clicking “I agree,” accessing oilbuddy.ai, or using any OilBuddy application, API, or demo environment, you agree to these Terms of Service (the “Terms”) and to the Privacy Policy and Cookie Policy, which are incorporated by reference.',
          'If you use the Service on behalf of a company, partnership, or other organization, you represent that you have authority to bind that organization, and “you” includes that organization.',
          'If you do not agree, you must not use the Service.',
        ],
      },
      {
        id: 'definitions',
        number: '2',
        heading: 'Definitions',
        bullets: [
          '“Service” means the OilBuddy websites, dashboards, APIs, and field tools, including dispatch, field tickets, tank watch, well-head sheets, daily reports, invoicing, payroll displays, safety modules, and the store.',
          '“Customer Content” means data, files, signatures, photos, and records you or your users submit, including tickets, tank levels, well readings, invoices, and crew information.',
          '“Company Workspace” means a service-company or similar organization account that can invite employees, contractors, and company men.',
          '“Field Ticket” means a digital or printable record of labor, equipment, materials, or hauls performed on a location.',
          '“Company Man” means a field representative authorized to review or approve tickets or location reports.',
          '“Operator” means the oil and gas operator or other customer named on a ticket, AFE, or PO. The Operator is not automatically an OilBuddy customer.',
        ],
      },
      {
        id: 'eligibility',
        number: '3',
        heading: 'Eligibility and field fitness',
        paragraphs: [
          'You must be legally able to enter a contract and, if you perform or record field work, meet all age, work-authorization, and site-access requirements of the applicable employer, contractor, and location.',
          'OilBuddy does not verify H2S, SafeLand, PEC, CDL, or other certifications unless a specific module is used to store what you enter. Possession of an OilBuddy login is not a work permit and is not evidence of competency.',
        ],
      },
      {
        id: 'accounts',
        number: '4',
        heading: 'Accounts, roles, and access control',
        paragraphs: [
          'You must provide accurate registration information and keep it current. You are responsible for all activity under your credentials.',
          'Role types (contractor, company, employee, driver, company man) control what you can see. You shall not attempt to escalate privileges, share logins on a crew trailer in a way that bypasses individual accountability, or access another company’s workspace without authorization.',
          'Company Workspace administrators are responsible for inviting and removing users promptly when a hand ends, a contractor is released, or a company man rotation changes.',
          'Demo accounts (including published demo logins) are for evaluation. Do not place live well, commercial, or personal data in a shared demo environment.',
        ],
      },
      {
        id: 'license',
        number: '5',
        heading: 'License to use the Service',
        paragraphs: [
          'Subject to these Terms and timely payment of any applicable fees, OilBuddy grants you a limited, non-exclusive, non-transferable, revocable license to access the Service for your internal oilfield business operations.',
          'You may not copy, modify, reverse engineer, resell, or provide the Service as a bureau to unaffiliated companies except as expressly allowed in a written order form. You may not use the Service to build a competing field-ticket or tank-watch product.',
        ],
      },
      {
        id: 'content',
        number: '6',
        heading: 'Customer Content and record integrity',
        paragraphs: [
          'You retain ownership of Customer Content. You grant OilBuddy a worldwide, non-exclusive license to host, display, transmit, and process Customer Content solely to provide and secure the Service and as described in the Privacy Policy.',
          'You represent that you have the right to enter and store the well names, operator names, AFEs, volumes, rates, and personnel data you submit, including under any MSA, confidentiality agreement, or operator IT policy.',
          'Field tickets, tank straps, load tickets, and well-head readings are business records. You agree that entries, quantities, hours, and signatures will be truthful to the best of your knowledge. Falsifying a ticket, inflating hours, or misstating barrels or water/oil hauls is a material breach and may be disclosed to the workspace owner.',
        ],
      },
      {
        id: 'not-advice',
        number: '7',
        heading: 'No engineering, HSE, or professional advice',
        paragraphs: [
          'THE SERVICE IS A RECORD-KEEPING AND WORKFLOW TOOL. IT DOES NOT PROVIDE PROFESSIONAL ENGINEERING, GEOLOGY, RESERVOIR, LEGAL, TAX, MEDICAL, OR SAFETY CERTIFICATION SERVICES.',
          'Tank visuals, flags, estimated barrels, and AI suggestions are aids only. They are not a certified strap table, not a custody-transfer measurement, and not a replacement for a qualified pumper, gauger, or measurement technician where those duties apply.',
          'You remain solely responsible for well control, H2S procedures, lockout/tagout, safe work permits, SPCC, waste handling, and compliance with OSHA, EPA, PHMSA (where applicable), state oil and gas commissions, and operator HSE manuals.',
        ],
      },
      {
        id: 'safety',
        number: '8',
        heading: 'Safety and regulatory compliance',
        paragraphs: [
          'You shall not rely on OilBuddy as your only HSE system of record unless you have independently determined that it meets your statutory and contractual obligations.',
          'If the Service is unavailable (no signal on location, outage, or device failure), you must continue to follow paper or operator-required backup procedures. Unavailability of OilBuddy is not an excuse for failing to complete a required ticket, JSA, or regulatory report.',
          'OilBuddy has no duty to notify government agencies of spills, injuries, or production events contained in your records unless required by law independently of these Terms.',
        ],
      },
      {
        id: 'employment',
        number: '9',
        heading: 'No employment or agency relationship with OilBuddy',
        paragraphs: [
          'OilBuddy is a software vendor. We are not the employer of contractors or employees who use the Service, not a staffing agency, not a motor carrier, and not a party to your MSA with an operator unless a separate written agreement says otherwise.',
          'Wage-and-hour compliance, 1099 versus W-2 classification, CDL and hours-of-service rules, insurance, and indemnities between operator, service company, and contractor are solely among those parties.',
          'Job marketplace listings are advertisements by the posting company. OilBuddy does not guarantee work, rates, or payment by an operator.',
        ],
      },
      {
        id: 'invoicing',
        number: '10',
        heading: 'Tickets, invoices, and payments',
        paragraphs: [
          'Converting a field ticket to an invoice, or creating an invoice with line items, is a convenience. You are responsible for the commercial accuracy of rates, quantities, taxes, and customer identity, and for any required operator coding (AFE, PO, cost center).',
          'OilBuddy does not guarantee that an operator or customer will pay an invoice generated in the Service. Payment processing, if enabled, is provided by third-party processors subject to their terms.',
          'Voiding an invoice does not delete audit history. You shall not use void or edit functions to conceal a record that must be preserved for a customer audit or legal hold.',
        ],
      },
      {
        id: 'ai-terms',
        number: '11',
        heading: 'AI and automated features',
        paragraphs: [
          'Optional AI features may draft text, summarize, or suggest actions. Output can be incomplete or incorrect. You must review all AI output before it is sent to a customer, attached to a ticket, or used for tax or safety decisions.',
          'You shall not input highly sensitive operator data into an AI feature if your MSA or operator IT policy forbids third-party model processing. Workspace administrators should disable or restrict AI features as required by their customers.',
        ],
      },
      {
        id: 'integrations',
        number: '12',
        heading: 'Third-party integrations and store',
        paragraphs: [
          'Integrations (including accounting, communications, and payments) and store or Amazon product listings are provided by third parties. OilBuddy is not responsible for their availability, pricing, or data handling. Enabling an integration constitutes instruction to share the data needed for that integration.',
          'Affiliate or store links may generate commissions. Product listings are not an endorsement of PPE or equipment for a particular well or H2S environment. You must follow your company’s approved-vendor and PPE matrix.',
        ],
      },
      {
        id: 'acceptable-use',
        number: '13',
        heading: 'Acceptable use',
        paragraphs: ['You shall not:'],
        bullets: [
          'Use the Service for any unlawful purpose, including fraud, false tickets, or concealment of environmental or safety events.',
          'Upload malware, scrape the Service, or overload APIs except through documented, authenticated use.',
          'Interfere with another customer’s workspace or attempt to read well, tank, or payroll data you are not authorized to see.',
          'Use the Service to store data you are contractually forbidden to place in a SaaS system.',
          'Misrepresent your identity, certifications, or authority to approve a ticket as company man or supervisor.',
        ],
      },
      {
        id: 'ip',
        number: '14',
        heading: 'Intellectual property',
        paragraphs: [
          'OilBuddy and its licensors own the Service, software, visual tank components, documentation, and trademarks (including OilBuddy and OilBuddy AI). These Terms do not transfer any ownership to you except the limited license in Section 5.',
          'Feedback you provide may be used by OilBuddy without restriction or compensation.',
        ],
      },
      {
        id: 'confidentiality',
        number: '15',
        heading: 'Confidentiality',
        paragraphs: [
          'Each party may receive non-public information from the other. The receiving party shall use the same care it uses for its own confidential information, and no less than reasonable care, and shall use the information only to perform under these Terms.',
          'Customer Content about wells, volumes, customers, and pricing is your confidential information. OilBuddy’s product design, non-public roadmaps, and security details are OilBuddy’s confidential information.',
          'This Section does not limit disclosures required by law, or information that is or becomes public through no fault of the receiving party.',
        ],
      },
      {
        id: 'fees',
        number: '16',
        heading: 'Fees, taxes, and suspension',
        paragraphs: [
          'If you subscribe to a paid plan, you agree to the fees, billing period, and taxes stated at purchase or on an order form. Unpaid invoices for the Service may result in suspension after notice.',
          'You are responsible for sales, use, and similar taxes except taxes on OilBuddy’s net income. Contractor invoices you issue to operators are your revenue, not OilBuddy’s, unless we separately contract to factor or collect.',
        ],
      },
      {
        id: 'warranty',
        number: '17',
        heading: 'Disclaimer of warranties',
        paragraphs: [
          'THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE.” TO THE MAXIMUM EXTENT PERMITTED BY LAW, OILBUDDY DISCLAIMS ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, NON-INFRINGEMENT, AND ANY WARRANTY THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SUITABLE AS A SOLE SYSTEM OF RECORD FOR REGULATORY FILINGS, CUSTODY TRANSFER, OR INCIDENT REPORTING.',
          'You acknowledge that oilfield connectivity is often poor and that you must maintain backup procedures.',
        ],
      },
      {
        id: 'liability',
        number: '18',
        heading: 'Limitation of liability',
        paragraphs: [
          'TO THE MAXIMUM EXTENT PERMITTED BY LAW, OILBUDDY AND ITS AFFILIATES, OFFICERS, AND SUPPLIERS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, GOODWILL, OR BUSINESS INTERRUPTION, OR FOR WELL DAMAGE, ENVIRONMENTAL RESPONSE COSTS, PERSONAL INJURY, OR PRODUCTION LOSSES, ARISING OUT OF THE SERVICE OR THESE TERMS, EVEN IF ADVISED OF THE POSSIBILITY.',
          'OILBUDDY’S TOTAL LIABILITY FOR ALL CLAIMS IN THE AGGREGATE SHALL NOT EXCEED THE AMOUNTS PAID BY YOU TO OILBUDDY FOR THE SERVICE IN THE TWELVE (12) MONTHS BEFORE THE CLAIM, OR ONE HUNDRED U.S. DOLLARS (US$100) IF YOU USE ONLY A FREE OR DEMO ACCOUNT.',
          'The foregoing does not limit liability that cannot be limited under applicable law, including certain bodily-injury claims where a waiver is unenforceable. OilBuddy is not responsible for the acts or omissions of operators, company men, or other users.',
        ],
      },
      {
        id: 'indemnity',
        number: '19',
        heading: 'Indemnification',
        paragraphs: [
          'You shall indemnify, defend, and hold harmless OilBuddy and its officers, employees, and agents from and against claims, damages, losses, and expenses (including reasonable attorneys’ fees) arising out of: (a) Customer Content, including alleged inaccuracy of tickets, tank volumes, or invoices; (b) your field operations, vehicles, or worksite conduct; (c) your breach of these Terms or of an operator or MSA confidentiality duty; (d) a claim that your use of the Service violated law; or (e) a dispute among operator, service company, contractor, and employee regarding pay, tickets, or access.',
        ],
      },
      {
        id: 'term',
        number: '20',
        heading: 'Term, suspension, and termination',
        paragraphs: [
          'These Terms start when you first use the Service and continue until terminated. You may stop using the Service at any time. We may suspend or terminate access immediately if you materially breach these Terms, if required by law, or if your use creates a security or operational risk to other customers.',
          'Upon termination, your license ends. We may delete or restrict Customer Content after a commercially reasonable export window, except records we must retain under the Privacy Policy or law. Sections that by their nature should survive (including 6–8, 14–19, 21–23) survive termination.',
        ],
      },
      {
        id: 'law',
        number: '21',
        heading: 'Governing law and venue',
        paragraphs: [
          'These Terms are governed by the laws of the State of North Dakota, excluding conflict-of-law rules. Subject to Section 22, exclusive venue lies in the state or federal courts located in North Dakota, and you consent to personal jurisdiction there.',
          'The U.N. Convention on Contracts for the International Sale of Goods does not apply.',
        ],
      },
      {
        id: 'disputes',
        number: '22',
        heading: 'Disputes',
        paragraphs: [
          'Before filing a claim, you agree to email contact@oilbuddy.ai and attempt in good faith to resolve the dispute for thirty (30) days. Either party may still seek injunctive relief in court to protect intellectual property, confidential well data, or unauthorized access.',
          'If a court finds a portion of these Terms unenforceable, the remainder stays in effect. These Terms are the entire agreement regarding the Service and supersede prior click-through terms, except a signed enterprise order form or MSA addendum that expressly overrides a section.',
        ],
      },
      {
        id: 'changes-tos',
        number: '23',
        heading: 'Changes to the Terms',
        paragraphs: [
          'We may update these Terms by posting a new revision on this page and updating the revision block. Material changes become effective thirty (30) days after posting, or immediately for new users. If you continue to use the Service after the effective date, you accept the revised Terms. If you do not agree, you must stop using the Service and request account closure.',
        ],
      },
      {
        id: 'misc',
        number: '24',
        heading: 'Miscellaneous',
        bullets: [
          'Notices to OilBuddy must be sent to contact@oilbuddy.ai with a copy of the relevant document ID (OB-LEG-TOS-001).',
          'You may not assign these Terms without our consent, except to a successor that assumes your oilfield business and obligations. We may assign to an affiliate or in connection with a corporate transaction.',
          'Failure to enforce a provision is not a waiver. These Terms do not create a partnership or joint venture.',
          'Headings are for convenience only.',
        ],
      },
      {
        id: 'contact-tos',
        number: '25',
        heading: 'Contact',
        paragraphs: [
          'OilBuddy AI — Legal notices: contact@oilbuddy.ai — Web: oilbuddy.ai/#/contact — Related documents: Privacy Policy (OB-LEG-PRIV-001), Cookie Policy (OB-LEG-COOK-001).',
        ],
      },
    ],
  },
  cookies: {
    key: 'cookies',
    title: 'Cookie and Tracking Technologies Policy',
    shortTitle: 'Cookies',
    documentId: 'OB-LEG-COOK-001',
    revision: 'Rev 2.0',
    effective: 'August 14, 2026',
    updated: 'August 14, 2026',
    classification: 'Public — Complements Privacy Policy OB-LEG-PRIV-001',
    summary:
      'This Cookie Policy explains how OilBuddy AI uses cookies, local storage, and similar technologies on oilbuddy.ai and related applications. In an oilfield environment, these technologies are used primarily so supervisors, contractors, drivers, and company men stay signed in on shared or personal devices — not for advertising.',
    sections: [
      {
        id: 'what',
        number: '1',
        heading: 'What cookies and similar technologies are',
        paragraphs: [
          'A cookie is a small text file stored on your device by a website. OilBuddy also uses HTML5 local storage and session storage for the same class of purposes: keeping you authenticated, remembering your user type (contractor, company, employee, driver, company man), and restoring the last safe application state after a refresh.',
          'These technologies may be first-party (set by oilbuddy.ai) or, if you enable an integration or play embedded media, third-party.',
        ],
      },
      {
        id: 'why',
        number: '2',
        heading: 'Why we use them',
        bullets: [
          'Strictly necessary — login tokens, CSRF/session integrity, and role routing so a company user is not sent into a contractor dashboard.',
          'Functional — remembering UI preferences such as sidebar state where implemented.',
          'Security — supporting detection of invalid tokens and forced re-authentication.',
          'Limited diagnostics — understanding failed page loads. We do not use advertising or cross-site marketing cookies.',
        ],
      },
      {
        id: 'types',
        number: '3',
        heading: 'Categories we set',
        subheads: [
          {
            heading: '3.1 Essential authentication',
            paragraphs: [
              'Examples of keys the web application may store locally include authentication tokens and user-type indicators required to call OilBuddy APIs. Without these, you cannot remain signed in. They are first-party and persist until you sign out, the token expires, or you clear site data.',
            ],
          },
          {
            heading: '3.2 Preference and role context',
            paragraphs: [
              'We may store non-sensitive preferences (for example crew level or last selected view) so a returning supervisor or driver lands on the correct field tools. These are not used to advertise third-party products.',
            ],
          },
          {
            heading: '3.3 Integrations and store',
            paragraphs: [
              'If you open a third-party integration or an Amazon / store listing, that third party may set its own cookies according to its policy. OilBuddy does not control those cookies. Do not enable integrations on a shared doghouse PC if your operator forbids third-party trackers.',
            ],
          },
        ],
      },
      {
        id: 'duration',
        number: '4',
        heading: 'Duration',
        bullets: [
          'Session technologies expire when you close the browser, unless “remember me” or a long-lived token is used.',
          'Persistent tokens remain until expiry, logout, or manual clearing. You should sign out at the end of a hitch if you used a shared device.',
          'Security logs associated with cookie-backed sessions are retained on a shorter cycle than field tickets, as described in the Privacy Policy.',
        ],
      },
      {
        id: 'control',
        number: '5',
        heading: 'How you can control cookies',
        paragraphs: [
          'Most browsers let you block or delete cookies and site storage. OilBuddy’s login will not function if essential storage is blocked. On a crew shared computer, use a private window or sign out and clear OilBuddy site data after use.',
          'Industry “Do Not Track” signals are not consistently implemented. OilBuddy does not change its essential cookie behavior in response to DNT; we already do not use advertising cookies.',
          'Mobile applications, if installed, may use secure device storage analogous to cookies. Uninstalling the app removes that storage.',
        ],
      },
      {
        id: 'changes-cook',
        number: '6',
        heading: 'Changes',
        paragraphs: [
          'We may update this Cookie Policy when we add features or when law requires. The revision block at the top is controlling. Material changes will be posted on this page.',
        ],
      },
      {
        id: 'contact-cook',
        number: '7',
        heading: 'Contact',
        paragraphs: [
          'Questions about cookies or device storage: contact@oilbuddy.ai. See also Privacy Policy OB-LEG-PRIV-001 and Terms of Service OB-LEG-TOS-001.',
        ],
      },
    ],
  },
}

